import { Component, forwardRef, input, signal } from '@angular/core';
import { type ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/** A single selectable option in a {@link RadioGroup}. */
export interface RadioOption<T = string> {
  label: string;
  value: T;
}

let uniqueId = 0;

/**
 * Reactive-forms radio group. Renders each option as a native radio (kept for keyboard and
 * screen-reader support) with a styled control on top, and reports the selected value through
 * `ControlValueAccessor`, so it plugs into `formControlName` like any native control.
 */
@Component({
  selector: 'app-radio-group',
  host: { class: 'radio-group', role: 'radiogroup' },
  templateUrl: './radio-group.html',
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => RadioGroup), multi: true },
  ],
})
export class RadioGroup implements ControlValueAccessor {
  readonly options = input<RadioOption[]>([]);

  /** Shared `name` so the native radios behave as one group. */
  protected readonly name = `radio-group-${(uniqueId += 1)}`;
  protected readonly value = signal<unknown>(null);
  protected readonly disabled = signal(false);

  private onChange: (value: unknown) => void = () => {
    /* empty */
  };
  private onTouched: () => void = () => {
    /* empty */
  };

  protected select(value: unknown): void {
    if (this.disabled()) {
      return;
    }

    this.value.set(value);
    this.onChange(value);
    this.onTouched();
  }

  protected touch(): void {
    this.onTouched();
  }

  writeValue(value: unknown): void {
    this.value.set(value);
  }

  registerOnChange(fn: (value: unknown) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }
}
